import { useAuthStore } from '@stores/auth.store';
import { useProjectStore } from '@stores/project.store';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Per-request opt-out flag for the project_id interceptor. Set on a
 * request config to suppress the auto-attached `?project_id=…` query
 * param — useful for endpoints that are intentionally scoped by JWT
 * only (e.g. `/auth/me`, `/projects` list, super-admin reads of
 * cross-org resources).
 *
 *   api.get('/projects', { skipProjectId: true })
 *
 * Augmenting axios' request config type lets callers pass this flag
 * directly without an `as` cast.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    skipProjectId?: boolean;
  }
  interface InternalAxiosRequestConfig {
    skipProjectId?: boolean;
  }
}


/**
 * Configured axios instance. Every API call in the app goes through this.
 *
 * Request interceptor: injects `Authorization: Bearer <token>`.
 * Response interceptor: on 401 → attempt one token refresh → retry the
 * original request. On refresh failure → force logout + redirect to /login.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// --- Request: inject bearer token -------------------------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Request: attach ?project_id= on GET requests ---------------------------
//
// Reads the active project from ProjectStore at request time (dynamic, not at
// module load) so it always reflects the user's current selection.
// Only attaches to GET requests. POST/PATCH/DELETE callers pass project_id in
// their body where needed, so we don't interfere.
// If the caller already specified params.project_id, we respect that value.

export function projectIdInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (config.method?.toUpperCase() !== 'GET') return config;
  if (config.skipProjectId) return config;

  const selectedProjectId = useProjectStore.getState().selectedProjectId;
  if (!selectedProjectId) return config;

  const existingParams = (config.params ?? {}) as Record<string, unknown>;
  if (existingParams.project_id !== undefined) return config;

  config.params = { ...existingParams, project_id: selectedProjectId };
  return config;
}

api.interceptors.request.use(projectIdInterceptor);

// --- Response: 401 → try refresh once → retry / force logout ----------------

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach((p) => (token ? p.resolve(token) : p.reject(error)));
  failedQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(normalizeError(error));
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) throw new Error('No refresh token');

      const { data } = await axios.post<{
        success: boolean;
        data: { accessToken: string; refreshToken: string };
      }>(`${api.defaults.baseURL}/auth/refresh`, { refresh_token: refreshToken });

      const tokens = data.data;
      const store = useAuthStore.getState();
      if (store.user) {
        store.setAuth({
          user: store.user,
          organization: store.organization,
          subscription: store.subscription,
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });
      }

      processQueue(null, tokens.accessToken);
      original.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return api(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// --- Error normalisation ----------------------------------------------------

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: string[];
  /** Raw response body — used for structured error branches (e.g. SESSION_WINDOW_CLOSED). */
  body?: Record<string, unknown>;
}

function normalizeError(err: AxiosError): ApiError {
  if (err.response) {
    const body = err.response.data as Record<string, unknown> | undefined;
    return {
      message:
        (typeof body?.message === 'string' ? body.message : null) ??
        'Something went wrong. Please try again.',
      statusCode: err.response.status,
      errors: Array.isArray(body?.errors) ? (body.errors as string[]) : undefined,
      body,
    };
  }
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return { message: 'Request timed out. Check your connection and try again.', statusCode: 0 };
  }
  return { message: 'Network error. Please check your connection.', statusCode: 0 };
}

export default api;
